/**
 * SceneSubMenu — Scene / lighting switcher panel content.
 *
 * Renders inside <SubMenuPanel> when activeSubMenu === 'scene'.
 * Migrated from the standalone SceneSwitcher component which previously
 * floated independently above the canvas. This version lives inside the
 * shared SubMenuPanel architecture.
 *
 * Visual layout (centered in the panel):
 *   ┌──────────────────────────────────────────┐
 *   │  [Day] [Night]    [Studio]               │  ← road pill + env buttons
 *   │              Day                          │  ← active scene label
 *   └──────────────────────────────────────────┘
 *
 * Groups scenes by engine preset type:
 *   • preset === 'road'  → pill toggle (day ↔ night) — mutually exclusive
 *   • preset !== 'road'  → individual glass buttons (studio, hdri, …)
 *
 * Gated: `features.environmentSwitch === true` (checked by MenuBar before
 * this component is rendered — component renders regardless but returns
 * null-safe if scenes are missing).
 *
 * @see docs/ims-brand-frontends/08-ui-screens.md#color--rim-submenu
 * @see packages/engine/src/api/configAdapter.ts — applyScene()
 */
import { memo, useCallback } from 'react';
import { applyScene } from '@ims/engine';
import type { Engine } from '@ims/engine';
import type { ResolvedConfig } from '@ims/shared';
import { cn } from '../../../utils/cn';
import { useAppStore } from '../../../store/app.store';
import { useUiTheme } from '../../../hooks/useUiTheme';

interface SceneSubMenuProps {
  config: ResolvedConfig | null;
  engineRef: React.RefObject<Engine | null>;
}

/** Human-readable label for a scene id. Extend via UITranslations later. */
function sceneLabel(id: string): string {
  const labels: Record<string, string> = {
    day: 'Day',
    night: 'Night',
    studio: 'Studio',
    road: 'Road',
  };
  return labels[id] ?? id.charAt(0).toUpperCase() + id.slice(1);
}

export const SceneSubMenu = memo(function SceneSubMenu({ config, engineRef }: SceneSubMenuProps) {
  const activeScene = useAppStore((s) => s.activeScene);
  const setActiveScene = useAppStore((s) => s.setActiveScene);
  const theme = useUiTheme();

  const scenes = config?.model.scenes;

  const switchToScene = useCallback(
    (sceneId: string) => {
      if (!scenes) return;
      if (sceneId === activeScene) return;
      const scene = scenes[sceneId];
      if (!scene) return;
      const engine = engineRef.current;
      if (engine) applyScene(engine, scene);
      setActiveScene(sceneId);
    },
    [activeScene, scenes, engineRef, setActiveScene],
  );

  if (!scenes) return null;

  const allIds = Object.keys(scenes);
  const roadIds = allIds.filter((id) => scenes[id].preset === 'road');
  const nonRoadIds = allIds.filter((id) => scenes[id].preset !== 'road');

  return (
    <div className="flex flex-col items-center gap-3 py-4">
      {/* ── Scene option buttons ──────────────────────────────────────── */}
      <div className="flex items-center gap-2">
        {/* Road group — pill with day ↔ night toggle */}
        {roadIds.length >= 2 && (
          <div
            className={`flex h-10 p-1 rounded-full ${theme.pill} backdrop-blur-[10px] gap-1 transition-colors duration-700`}
          >
            {roadIds.map((id) => (
              <button
                key={id}
                onClick={() => switchToScene(id)}
                aria-pressed={activeScene === id}
                className={cn(
                  'h-8 px-4 rounded-full text-white text-sm font-medium',
                  'transition-colors duration-200 cursor-pointer',
                  activeScene === id ? theme.tabActive : theme.tabInactive,
                )}
              >
                {sceneLabel(id)}
              </button>
            ))}
          </div>
        )}

        {/* Single road scene — standalone pill */}
        {roadIds.length === 1 && (
          <button
            onClick={() => switchToScene(roadIds[0])}
            aria-pressed={activeScene === roadIds[0]}
            className={cn(
              'h-10 px-4 rounded-full text-white text-sm font-medium',
              theme.glassBtn,
              'backdrop-blur-[10px] transition-colors duration-700 cursor-pointer',
              activeScene === roadIds[0] ? theme.tabActive : theme.glassBtnHover,
            )}
          >
            {sceneLabel(roadIds[0])}
          </button>
        )}

        {/* Non-road scenes — studio, hdri, … — individual buttons */}
        {nonRoadIds.map((id) => (
          <button
            key={id}
            onClick={() => switchToScene(id)}
            aria-pressed={activeScene === id}
            className={cn(
              'h-10 px-4 rounded-full text-white text-sm font-medium',
              theme.glassBtn,
              'backdrop-blur-[10px] transition-colors duration-700 cursor-pointer',
              activeScene === id ? theme.tabActive : theme.glassBtnHover,
            )}
          >
            {sceneLabel(id)}
          </button>
        ))}
      </div>

      {/* ── Active scene label ─────────────────────────────────────────── */}
      {activeScene && (
        <p className="text-sm font-bold text-white tracking-wide">{sceneLabel(activeScene)}</p>
      )}
    </div>
  );
});
